<template>
  <div
    class="card-list"
    :style="{
      '--visible-rows': visibleRows || false,
    }">
    <div
      v-if="$slots.title"
      class="card-list__title">
      <slot name="title" />
    </div>

    <div class="card-list-container">
      <ul
        class="card-list__list"
        :class="[{
          'card-list__list--visible-rows': !!visibleRows,
          'card-list__list--load-more': showLoadMore,
          'card-list__list--scrollable scrollbar-hidden anti-content': isScrollable,
        }]">
        <li
          v-for="(item, itemIndex) in itemsOrPlaceholders"
          :key="itemIndex"
          class="card-list__item">
          <template v-if="!isLoading">
            <slot
              name="card"
              v-bind="{ item, itemIndex, isMobile }" />
          </template>

          <template v-else>
            <slot
              name="skeletonCard" />
          </template>
        </li>

        <li
          v-if="showTagLink"
          :style="userDevice.innerWidth <= 1807 ? {} : {'grid-column-start': itemTagColumnStart}"
          class="card-list__item-tag card-list__item-tag-desktop">
          <a
            :href="showTagLink">
            <slot name="tagBlock" />
          </a>
        </li>

        <li
          v-if="tagBlockIndex === -1 && showTagLink"
          class="card-list__item">
          <a
            class="card-list__item-tag"
            :href="showTagLink">
            <slot name="tagBlock" />
          </a>
        </li>

        <li
          v-if="showNeedHelp"
          class="card-list__need-help">
          <slot name="needHelp">
            <need-help />
          </slot>
        </li>

        <li
          v-if="showLoadMore"
          class="card-list__load-more">
          <slot name="loadMore">
            <div
              class="load-more-list"
              :class="{'load-more-list--overlap': showLoadMoreOverlap}">
              <div class="load-more-list__wrapper">
                <div
                  v-if="showLoadMoreGradient && !isScrollable"
                  class="load-more-list__gradient" />
                <div
                  key="load-more"
                  :class="showLoadMoreClasses"
                  class="load-more-list__button button-round-bordered"
                  @click="emitLoadMore">
                  {{ showLoadMoreText }}
                </div>
              </div>
            </div>
          </slot>
        </li>

        <li
          v-if="infinityScroll"
          class="card-list__intersect">
          <span
            key="intersector"
            v-intersected="{ handler: intersectionHandler }"
            class="list-intersect" />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import intersectedDirective from 'Root/directives/intersectedDirective';
import NeedHelp from 'Root/pages/game/components/NeedHelp.vue';
import { mapState } from 'vuex';

export default {
  name: 'BaseCardList',

  components: {
    NeedHelp,
  },

  directives: {
    ...intersectedDirective,
  },

  props: {
    // Массив элементов для отображения
    items: {
      type: Array,
      required: true,
    },

    // Флаг: показывать кнопку Load more (или слот вместо нее)
    showLoadMore: {
      type: Boolean,
      default: false,
    },

    // Флаг: показывать градиент под кнопкой Load more
    showLoadMoreGradient: {
      type: Boolean,
      default: false,
    },

    // Флаг: перекрывать блоком "load more" последний ряд карточек
    showLoadMoreOverlap: {
      type: Boolean,
      default: false,
    },

    // Текст в кнопке "Load more"
    showLoadMoreText: {
      type: String,
      default: 'View more',
    },

    // Классы на кнопке "Load more"
    showLoadMoreClasses: {
      type: [Object, Array],
      default: null,
    },

    // Количество видимых строк
    visibleRows: {
      type: Number,
      default: 0,
    },

    // С какой ширины экрана делать список скроллящимся горизонтально, px
    scrollableFrom: {
      type: Number,
      default: 0,
    },

    // До какой ширины экрана делать список скроллящимся горизонтально, px
    scrollableTo: {
      type: Number,
      default: 0,
    },

    // Флаг: генерировать событие load-more при прокрутке до конца списка
    infinityScroll: {
      type: Boolean,
      default: false,
    },

    // Флаг: показывать блок "Need help?"
    showNeedHelp: {
      type: Boolean,
      default: false,
    },

    // Показывать тэг-линк из конструктора каталога
    showTagLink: {
      type: String,
      default: '',
    },

    // С какой ширины экрана отображать мобильную версию карточек, px
    mobileFrom: {
      type: Number,
      default: 1024,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(['userDevice']),

    itemTagColumnStart() {
      const itemsCount = this.itemsOrPlaceholders.length;
      return itemsCount >= 5 ? 5 : itemsCount + 1;
    },

    tagBlockIndex() {
      return this.isScrollable || this.isMobile ? -1 : 0;
    },

    isScrollable() {
      return this.userDevice.innerWidth <= this.scrollableFrom && this.userDevice.innerWidth >= this.scrollableTo;
    },

    isMobile() {
      return this.userDevice.innerWidth < this.mobileFrom;
    },

    itemsOrPlaceholders() {
      if (this.isLoading && !this.items.length) {
        return Array(5).fill({});
      }
      return this.items;
    },
  },

  methods: {
    intersectionHandler([entry]) {
      if (entry.isIntersecting && this.infinityScroll) {
        this.emitLoadMore();
      }
    },

    emitLoadMore() {
      this.$emit('load-more');
    },
  },
};
</script>

<style lang="scss" scoped>
  .card-list {
    --item-width: 280px;
    --grid-gap: 16px;
    --show-more-gradient-height: 60px;
    --show-more-overlap-padding: 40px;
    --title-font-size: 21px;
    --title-font-weight: 500;
    --title-margin-bottom: 20px;
  }

  .card-list-container {
    display: flex;
  }

  .card-list__title {
    font-size: var(--title-font-size);
    font-weight: var(--title-font-weight);
    margin-bottom: var(--title-margin-bottom);
  }

  .card-list__list {
    display: grid;
    flex-grow: 1;
    grid-template-columns: repeat(auto-fill, minmax(var(--item-width, 280px), 1fr));
    grid-gap: var(--grid-gap);
  }

  /* Visible rows */
  .card-list__list--visible-rows {
    grid-gap: 0 var(--grid-gap);
    grid-template-rows: repeat(var(--visible-rows, 1), max-content) repeat(100, 0);
    overflow-y: hidden;

    .card-list__item,
    .card-list__need-help {
      margin-bottom: var(--grid-gap);
    }
  }

  .card-list__item-tag {
    display: flex;
    grid-column: -2;
    grid-row: 1;
    padding: 24px;
    border-radius: 10px;
    height: 100%;
    background: linear-gradient(90deg, rgba($color--basic-blue, 0.25) 0%, rgba($color--basic-blue, 0.00) 100%);
  }

  .card-list__item-tag-desktop {
    height: auto;
    width: var(--item-width);
    margin-bottom: var(--grid-gap);
  }

  /* Load More */
  .card-list__list--load-more {
    grid-template-rows: repeat(var(--visible-rows, 1), max-content) max-content repeat(100, 0);
  }

  .card-list__load-more {
    z-index: 2;
    grid-column: 1 / -1;
    grid-row: calc(1 + var(--visible-rows, 1));
  }

  .load-more-list {
    position: relative;
  }

  .load-more-list__wrapper {
    display: grid;
  }

  .load-more-list__gradient {
    pointer-events: none;
    position: absolute;
    width: 100%;
    height: var(--show-more-gradient-height);
    bottom: 0;
    left: 0;
    background: linear-gradient(180deg, rgba($color--basic-black, 0) 0%, $color--basic-black 100%);
    z-index: 1;
  }

  .load-more-list__button {
    justify-self: center;
    align-self: center;
    font-weight: 500;
    scroll-snap-align: start;
    z-index: 2;
  }

  /* Load more overlap */
  .load-more-list--overlap {
    .load-more-list__gradient {
      bottom: 100%;
    }

    .load-more-list__wrapper {
      position: absolute;
      width: 100%;
      bottom: 100%;
      left: 0;
      background-color: $color--basic-black;
      padding-bottom: var(--show-more-overlap-padding);
    }
  }

  /* Scrollable */
  .card-list__list--scrollable {
    @include scroll-snap-parent;

    scroll-padding-left: var(--padding);
    padding-left: var(--padding);
    padding-right: var(--padding);
    overflow-x: scroll;
    grid-auto-flow: column;
    grid-auto-columns: var(--item-width);

    .card-list__item {
      scroll-snap-align: start;
      grid-row: 1;
    }

    .card-list__load-more {
      grid-column: initial;
      grid-row: 1;
      display: grid;
      align-items: center;
    }

    .load-more-list__wrapper {
      position: initial;
      padding-bottom: 0;
    }

    .load-more-list__gradient {
      display: none;
    }
  }

  /* Need help */
  .card-list__need-help {
    grid-column: 1 / -1;
  }

  .card-list__need-help:not(:nth-child(-n + 5)) {
    grid-row: 4;
  }

  @media screen and #{$screen-less-than-1024} {
    .card-list__item-tag-desktop {
      display: none;
    }
  }

  @media screen and (min-width: 800px) {
    .card-list__need-help {
      grid-row: auto !important;
    }

    .card-list__need-help:not(:nth-child(-n + 6)) {
      grid-row: 3 !important;
    }
  }

  @media screen and #{$screen-less-than-480} {
    .card-list__list--visible-rows {
      grid-template-rows: auto;
    }
  }
</style>
